import dayjs from "dayjs";
import { formatInr } from "../../shared/utils/currency";
import { todayInput } from "../../shared/utils/date";
import { balanceText, type BalanceDisplayInfo } from "../../shared/utils/balanceDisplay";

export type VoucherKind = "SalesInvoice" | "PurchaseInvoice" | "ReceiptVoucher" | "PaymentVoucher" | "JournalEntry";

export type LineRow = {
  key: string;
  ledgerName: string;
  partyId?: string | number | null;
  partyLabel?: string;
  againstBillId?: number | null;
  dueDate?: string;
  sacCode?: string;
  quantity: number;
  rate: number;
  amount?: number;
  per: string;
  gstPercent: number;
  tdsPercent: number;
  remarks?: string;
  billWiseEntries?: BillWiseEntry[];
};

export type BillWiseEntry = {
  type: "NewRef" | "AgainstRef" | "Advance" | "OnAccount";
  name: string;
  dueDate?: string;
  amount: number;
  drCr: "Dr" | "Cr";
  againstBillId?: number | null;
};

export type PendingBill = {
  id: number;
  billNo: string;
  billDate: string;
  outstanding: number;
  grandTotal?: number;
  paidAmount?: number;
  balanceType?: string;
  voucherType: string;
};

export type AllocationRow = { billId: number; allocatedAmount: number; remarks?: string };
export type BalanceInfo = BalanceDisplayInfo & {
  closingBalance?: number;
  balance?: number;
  balanceType?: string;
  type?: string;
  facilityType?: string;
  overdraftLimit?: number;
  drawingPower?: number;
  allowOverLimit?: boolean;
  overLimitTolerance?: number;
  availableOverdraftLimit?: number;
};
export type PreviewInfo = { taxableAmount?: number; cgstTotal?: number; sgstTotal?: number; igstTotal?: number; tdsTotal?: number; grandTotal?: number; totalAmount?: number };

export function newLine(): LineRow {
  return {
    key: crypto.randomUUID(),
    ledgerName: "",
    partyId: null,
    partyLabel: "",
    againstBillId: null,
    dueDate: "",
    quantity: 1,
    rate: 0,
    amount: 0,
    per: "NOS",
    gstPercent: 0,
    tdsPercent: 0,
    billWiseEntries: [{ type: "NewRef", name: "", dueDate: "", amount: 0, drCr: "Cr", againstBillId: null }]
  };
}

export function extractId(value: any) {
  return value?.id ?? value ?? null;
}

export function validateVoucher(values: any, lines: LineRow[], allocations: AllocationRow[], isInvoice: boolean, isBankVoucher: boolean, fallbackTotal: number) {
  const partyId = extractId(values.partyId);
  if (isInvoice && !partyId) return "Party is required";
  if (isBankVoucher && !extractId(values.bankAccountId)) return "Bank account is required";
  const voucherRows = lines.filter((row) => extractId(row.partyId) || Number(row.amount ?? row.rate ?? 0) > 0);
  const activePartyId = partyId ?? extractId(voucherRows[0]?.partyId);
  if (isBankVoucher && values.adjustmentMethod === "AgainstRef" && !activePartyId) return "Against Ref requires a party";
  if (isBankVoucher && values.adjustmentMethod === "AgainstRef" && allocations.length === 0) return "Select at least one pending bill allocation";
  if (isBankVoucher && !voucherRows.length) return "Add at least one voucher row";
  if (isBankVoucher && voucherRows.some((row) => !extractId(row.partyId) || Number(row.amount ?? row.rate ?? 0) <= 0)) {
    return "Each voucher row needs a party and amount";
  }
  const voucherAmount = Number(values.amount ?? voucherRows.reduce((sum, row) => sum + Number(row.amount ?? row.rate ?? 0), 0));
  if (isBankVoucher && voucherAmount <= 0) return "Amount must be greater than zero";
  if (isBankVoucher && values.adjustmentMethod === "AgainstRef") {
    const allocated = allocations.reduce((sum, row) => sum + Number(row.allocatedAmount ?? 0), 0);
    if (allocated <= 0) return "Allocated amount must be greater than zero";
    if (allocated > Number(voucherAmount ?? allocated)) return "Allocated amount cannot exceed voucher amount";
  }
  if (!isInvoice) return null;
  const usableLines = lines.filter((row) => row.ledgerName || row.sacCode || Number(row.quantity ?? 0) !== 0 || Number(row.rate ?? 0) !== 0 || Number(row.amount ?? 0) !== 0 || row.remarks);
  if (!usableLines.length) return "Add at least one line item";
  const invalidLine = usableLines.find((row) => {
    const amount = Number(row.amount ?? 0) || (Number(row.quantity ?? 0) * Number(row.rate ?? 0));
    return !row.ledgerName || amount === 0 || (Number(row.quantity ?? 0) > 0 && !row.per);
  });
  if (invalidLine) return "Each invoice line needs an item/ledger and non-zero amount; unit is required when quantity is used";
  if (fallbackTotal === 0) return "Invoice total cannot be zero";
  return null;
}

export function buildPayload({
  values,
  lines,
  allocations,
  isInvoice,
  isBankVoucher,
  voucherType,
  tempKey,
  title,
  fallbackTotal
}: {
  values: any;
  lines: LineRow[];
  allocations: AllocationRow[];
  isInvoice: boolean;
  isBankVoucher: boolean;
  voucherType: VoucherKind;
  tempKey: string;
  title: string;
  fallbackTotal: number;
}) {
  const amount = Number(values.amount ?? fallbackTotal ?? 0);
  const voucherRows = lines.filter((row) => extractId(row.partyId) || Number(row.amount ?? row.rate ?? 0) > 0);
  const firstVoucherPartyId = extractId(voucherRows[0]?.partyId);
  const voucherAmount = voucherRows.reduce((sum, row) => sum + Number(row.amount ?? row.rate ?? 0), 0);
  const invoiceLines = isInvoice ? lines.filter((row) => row.ledgerName || row.rate || row.amount).map((row, index) => ({
    rowNo: index + 1,
    ledgerName: row.ledgerName,
    sacCode: row.sacCode ?? values.sacCode ?? "",
    quantity: row.quantity,
    rate: row.rate,
    per: row.per,
    amount: Number(row.amount ?? 0) || (Number(row.quantity ?? 0) * Number(row.rate ?? 0)),
    gstPercent: row.gstPercent,
    tdsPercent: row.tdsPercent,
    remarks: row.remarks
  })) : voucherRows.map((row, index) => {
    const rowAmount = Number(row.amount ?? row.rate ?? 0);
    return {
      rowNo: index + 1,
      ledgerName: row.partyLabel || row.ledgerName || `Party ${extractId(row.partyId) ?? index + 1}`,
      partyId: extractId(row.partyId),
      sacCode: "",
      quantity: 1,
      rate: rowAmount,
      per: "NOS",
      amount: rowAmount,
      gstPercent: 0,
      tdsPercent: 0,
      remarks: row.remarks
    };
  });

  return {
    voucherType,
    billNo: values.billNo ?? "",
    billDate: values.billDate?.format("YYYY-MM-DD") ?? todayInput(),
    dueDate: values.dueDate?.format("YYYY-MM-DD"),
    partyId: extractId(values.partyId) ?? firstVoucherPartyId,
    ledgerAccountId: extractId(values.ledgerAccountId),
    bankAccountId: extractId(values.bankAccountId),
    sacCode: values.sacCode,
    referenceNo: values.referenceNo,
    adjustmentMethod: values.adjustmentMethod ?? "NewRef",
    adjustmentEntries: isBankVoucher && values.adjustmentMethod === "AgainstRef"
      ? allocations.map((row) => ({
        method: "AgainstRef",
        partyId: extractId(values.partyId) ?? firstVoucherPartyId,
        againstBillId: row.billId,
        allocatedAmount: row.allocatedAmount,
        remarks: row.remarks
      }))
      : isBankVoucher && voucherAmount ? [{
        method: values.adjustmentMethod ?? "OnAccount",
        partyId: extractId(values.partyId) ?? firstVoucherPartyId,
        allocatedAmount: voucherAmount,
        remarks: values.narration
      }] : [],
    lineItems: invoiceLines,
    tempKey
  };
}

export { balanceText };

export function validateOverdraftPayment(voucherType: VoucherKind, totalAmount: number, balance?: BalanceInfo) {
  const source = balance as (BalanceInfo & {
    FacilityType?: string;
    AvailableOverdraftLimit?: number;
    AllowOverLimit?: boolean;
    OverLimitTolerance?: number;
  }) | undefined;
  const facility = source?.facilityType ?? source?.FacilityType;
  if (voucherType !== "PaymentVoucher" || !facility || facility === "Normal") return null;
  const available = Number(source?.availableOverdraftLimit ?? source?.AvailableOverdraftLimit ?? 0);
  const tolerance = Number(source?.overLimitTolerance ?? source?.OverLimitTolerance ?? 0);
  const allow = Boolean(source?.allowOverLimit ?? source?.AllowOverLimit);
  if (!allow && available - totalAmount < 0) return `Payment exceeds available overdraft limit by ${formatInr(Math.abs(available - totalAmount))}`;
  if (allow && available - totalAmount + tolerance < 0) return `Payment exceeds overdraft tolerance by ${formatInr(Math.abs(available - totalAmount + tolerance))}`;
  return null;
}

export function serializeDraftValues(values: Record<string, any>) {
  return Object.fromEntries(Object.entries(values).map(([key, value]) => {
    if (dayjs.isDayjs(value)) return [key, value.format("YYYY-MM-DD")];
    return [key, value];
  }));
}

export function hydrateDraftValues(values: Record<string, any>) {
  return {
    ...values,
    billDate: values.billDate ? dayjs(values.billDate) : dayjs(),
    dueDate: values.dueDate ? dayjs(values.dueDate) : undefined
  };
}
